import { AppProvider } from '@ifixit/app/hooks';
import { createRoot } from 'react-dom/client';
import { store } from 'Shared/ifixit_store';
import { Provider } from 'react-redux';
import { withErrorInfo } from 'Shared/logging';
import { initializeReactQuery } from 'Shared/react-query-initialize';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { getGrowthbook } from 'Shared/features';
import { ViewLangIntlProvider } from 'Shared/use_intl';
import { onDomReady } from 'Shared/utils/dom-ready';
import InstantSearchHero from './search_hero/instant_search_hero';
import { WPCampaignBanner } from 'Shared/Campaigns/campaign-banner-wp';

type HomePageHeroProps = {
   type: string;
   prompt: string;
   hasCampaigns: boolean;
   campaignEnabled: boolean;
   heroImagePath: string;
   sizes: {
      intermediate_width: number;
      small_width: number;
      space: number;
      default: number;
      small: number;
      intermediate: number;
   };
};

const HomePageHero = (props: HomePageHeroProps) => {
   // Only render the interactive content (search box)
   // The image and overlay are already in the placeholder and should not be replaced
   const fallbackSearchHero = (
      <>
         <style>{`
            .home-page-hero-flex { height: ${props.sizes.default}px; }
            @media (min-width: 768px) { .home-page-hero-flex { height: ${props.sizes.small}px; } }
            @media (min-width: 1024px) { .home-page-hero-flex { height: ${props.sizes.intermediate}px; } }
         `}</style>
         <div className="home-page-hero-flex flex flex-col justify-center items-center pt-3 pb-5 md:pb-3 relative overflow-hidden w-full">
            <div className="flex flex-col gap-2 items-center text-base relative z-[1]">
               <InstantSearchHero {...props} />
            </div>
         </div>
      </>
   );

   return (
      <div className="relative">
         {props.campaignEnabled ? (
            <WPCampaignBanner {...props} fallbackComponent={fallbackSearchHero} />
         ) : (
            fallbackSearchHero
         )}
      </div>
   );
};

// Custom initialization that preserves the placeholder image
// and only renders React content into the .hero-content container
// This prevents the flash when React replaces the placeholder image
onDomReady(() => {
   const selector = `.react-component[data-name="HomePageHero"]`;
   const placeholders = document.querySelectorAll(selector);

   Array.from(placeholders).forEach(placeholder => {
      const element = placeholder as HTMLElement;
      // Find the hero-content container inside the placeholder
      // The image is in .hero-image and should not be replaced
      const heroContent = element.querySelector('.hero-content');
      if (!heroContent) {
         // Fallback: if structure is different, skip (should not happen)
         console.warn('HomePageHero: .hero-content not found');
         return;
      }

      const props = {
         ...JSON.parse(element.dataset.props || '{}'),
      };

      withErrorInfo(async () => {
         const root = createRoot(heroContent);
         const growthbook = await getGrowthbook();
         root.render(
            initializeReactQuery(
               <ViewLangIntlProvider>
                  <GrowthBookProvider growthbook={growthbook}>
                     <Provider store={store}>
                        <AppProvider
                           ifixitOrigin={window.origin}
                           isLoggedIn={App.isLoggedIn}
                           priceTiers={App.priceTiers}
                           isOnMobileApp={App.isOnMobileApp}
                           storeLocale={App.userPreferredStoreLocale}
                        >
                           <HomePageHero {...props} />
                        </AppProvider>
                     </Provider>
                  </GrowthBookProvider>
               </ViewLangIntlProvider>
            )
         );
      }, ['Error occurred while rendering HomePageHero', props]);
   });
});
